import { AlertTriangle, Info } from "lucide-react";
import { Link } from "react-router";
import { describeSubscription, useBillingState } from "../../lib/use-billing-state.js";
import { useDismissPopover, usePopover } from "./account-avatar.js";
import { HeaderGlyph } from "./glyph.js";

/**
 * Billing warning as a header glyph, sitting left of the account menu.
 *
 * This replaces a full-width banner that used to render above the router.
 * Being a block in the layout flow, it pushed every page down by its own
 * height — and only on pages whose header participates in that flow, so the
 * home screen (fixed header) stayed put while every other page shifted. The
 * result was a chrome that moved around depending on which route you were on.
 *
 * As a glyph the warning costs no vertical space, appears in exactly the same
 * spot on every page including home, and still carries its severity through
 * icon colour. The message itself moves into a popover on click.
 *
 * Display rules (unchanged from the old banner):
 *   • `past_due` / `paused`     → red, always shown
 *   • `cancel_at_period_end`    → blue, shown when ≤ 30 days left
 *   • trialing                  → blue, shown when ≤ 7 days left
 *   • active, daysLeft ≤ 3      → red
 *   • active, daysLeft ≤ 14     → yellow
 *   • otherwise                 → nothing rendered
 */
export function ExpiryGlyph() {
  const popover = usePopover();
  const ref = useDismissPopover(popover.open, popover.close);
  const notice = useExpiryNotice();

  if (!notice) return null;

  const label =
    notice.tone === "red"
      ? "Warnung zur Abrechnung"
      : notice.tone === "yellow"
        ? "Hinweis zur Abrechnung"
        : "Information zur Abrechnung";

  return (
    <div ref={ref} className="relative">
      <HeaderGlyph
        label={label}
        tone={notice.tone === "red" ? "danger" : notice.tone === "yellow" ? "warn" : "info"}
        pressed={popover.open}
        onClick={popover.toggle}
      >
        {notice.tone === "blue" ? (
          <Info className="size-5" aria-hidden />
        ) : (
          <AlertTriangle className="size-5" aria-hidden />
        )}
      </HeaderGlyph>

      {popover.open && (
        <div
          role="status"
          aria-label={label}
          className="absolute right-0 z-50 mt-2 w-72 overflow-hidden rounded-2xl border border-white/10 bg-background/95 shadow-floating backdrop-blur-floating"
        >
          <p className="px-4 pt-3.5 text-body-sm leading-snug" style={{ color: "#f4f5f3" }}>
            {notice.message}
          </p>
          <div className="px-4 pb-3.5 pt-3">
            <Link
              to="/billing"
              onClick={popover.close}
              className="inline-flex items-center rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-body-sm font-medium transition-colors hover:bg-white/20"
            >
              Verwalten
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

interface ExpiryNotice {
  tone: "yellow" | "red" | "blue";
  message: string;
}

/**
 * Resolve the current subscription into a severity + message, or null when
 * there is nothing worth interrupting the coach about.
 */
function useExpiryNotice(): ExpiryNotice | null {
  const billing = useBillingState();
  const state = billing.data;
  // Manual license overrides Stripe: no Stripe-based billing warnings.
  if (state?.effective?.kind === "manual") return null;
  if (!state?.subscription) return null;
  const days = describeSubscription(state).daysLeft;
  const status = state.subscription.status;

  if (status === "past_due") {
    return {
      tone: "red",
      message:
        "Deine letzte Zahlung ist fehlgeschlagen. Aktualisiere deine Zahlungsmethode, sonst wird die Lizenz bald gesperrt.",
    };
  }
  if (status === "paused") {
    return {
      tone: "red",
      message:
        "Die Testphase ist abgelaufen, ohne dass eine Zahlungsmethode hinterlegt wurde. Schließe die Bezahlung ab, um weiterzumachen.",
    };
  }
  if (state.subscription.cancelAtPeriodEnd && days !== null && days <= 30) {
    return {
      tone: "blue",
      message: `Subscription gekündigt — läuft in ${days} Tag${days === 1 ? "" : "en"} aus.`,
    };
  }
  if (status === "trialing" && days !== null && days <= 7) {
    return {
      tone: "blue",
      message: `Testphase endet in ${days} Tag${days === 1 ? "" : "en"}. Lege jetzt eine Zahlungsmethode an.`,
    };
  }
  if (status === "active" && days !== null) {
    if (days <= 3) {
      return {
        tone: "red",
        message: `Abrechnungszeitraum endet in ${days} Tag${days === 1 ? "" : "en"}.`,
      };
    }
    if (days <= 14) {
      return { tone: "yellow", message: `Abrechnungszeitraum endet in ${days} Tagen.` };
    }
  }
  return null;
}
